import { Navigate, useNavigate } from 'react-router-dom';
import MetrcProductsDialog from '../../MetrcProductsDialog';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import { useSsoReauth } from '../../contexts/SsoReauthContext';
import { importProduct } from '../../hooks/useMetrcImports';
import { useImportsWizard } from './ImportsWizardContext';
import { getSafeEndpointMessage, isSsoReauthRequired } from '../../utils/requestErrors';
import { getAvailableActions, getWriteActions } from '../../utils/permissionCatalog';
import { checkPermissionActions } from '../../utils/permissions';

export default function MetrcProductsContainer() {
  const navigate = useNavigate();
  const { org, currentUser } = useLegacyAppSession();
  const { handleSsoError } = useSsoReauth();
  const {
    metrcAuthData,
    metrcFacilitiesSelected,
    resetMetrcWizard,
    returnTo,
    workflow,
  } = useImportsWizard();
  const actions = getAvailableActions(currentUser);
  const canImportProducts =
    checkPermissionActions(actions, getWriteActions('Products')) &&
    checkPermissionActions(actions, getWriteActions('Imports'));

  if (!metrcAuthData) {
    return <Navigate to="/imports/metrc/auth" replace />;
  }

  if (!metrcFacilitiesSelected.length) {
    return <Navigate to="/imports/metrc/select" replace />;
  }

  if (workflow === 'facilities') {
    return <Navigate to={returnTo} replace />;
  }

  return (
    <MetrcProductsDialog
      organizationId={Number(org?.id) || undefined}
      marketId={metrcAuthData.marketId}
      username={metrcAuthData.username}
      market={metrcAuthData.market}
      facilities={metrcFacilitiesSelected}
      onClose={() => {
        resetMetrcWizard();
        navigate(returnTo, { replace: true });
      }}
      onBack={() => navigate('/imports/metrc/select')}
      onSave={async ({ selectedProducts }) => {
        if (!org?.id) return;
        if (!canImportProducts) return;

        try {
          await Promise.all(
            (selectedProducts || []).map((product) =>
              importProduct({
                organizationId: org.id,
                metrcProductId: product.id,
                facilityId: product.dbFacilityId,
                productId: product.dbProduct?.id,
              })
            )
          );

          resetMetrcWizard();
          showToastSuccess('Products Successfully imported');
          navigate(returnTo, { replace: true });
        } catch (error) {
          console.error(error);
          // The wizard state does not survive the sign-in redirect, so the user comes back to where the import started.
          if (handleSsoError(error, { returnTo })) return;
          if (isSsoReauthRequired(error)) {
            // No stored Metrc sign-in works any more; the auth screen offers the sign-in again.
            showToastError(getSafeEndpointMessage(error, 'Metrc sign-in is required.'));
            navigate('/imports/metrc/auth', { replace: true, state: { returnTo, workflow } });
            return;
          }
          showToastError('One or more products could not be imported. Some products may have imported successfully.');
        }
      }}
    />
  );
}
